import React, { useState } from 'react';

import { SourceBook } from "@dndModels";

import MyTTRPGAPI from "@utilities/smart_api";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import StringBox from '@components/input/strings/StringBox';
import CheckBox from "@components/input/CheckBox";
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from './DandDButtonBoxSimple';

const api = MyTTRPGAPI.getInstance();

interface Props {
  open: boolean;
  item_name: string; // What they were trying to create, ie "Spell"
  onClose: () => void;
  onCreated: (sb: SourceBook) => void;
}

const abbreviate = (name: string) => {
  return name.split(/\s+/).filter(o => o.length > 0).map(o => o[0].toUpperCase()).join("");
}

// Everything a non-admin creates goes in one of their own source books,
// so when they don't have one yet they have to make one first.
export default function CreateFirstSourceBookModal({
  open,
  item_name,
  onClose,
  onCreated
}: Props) {
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [is_public, setIsPublic] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const use_abbreviation = abbreviation.trim() !== "" ? abbreviation.trim() : abbreviate(name);

  const create = async () => {
    setProcessing(true);
    setError("");
    const sb = new SourceBook();
    sb.name = name.trim();
    sb.abbreviation = use_abbreviation;
    sb.source_book_type = "Homebrew";
    sb.is_free = true;
    sb.third_party = true;
    sb.is_public = is_public;
    // createObject sets the owner and adds it to their owned source books (and default).
    const res = await api.upsertObject("source_book", sb);
    setProcessing(false);
    if (res && sb._id !== "") {
      onCreated(sb);
    } else {
      setError("Something went wrong creating your source book. Please try again.");
    }
  }

  return (
    <ModalOrDrawer open={open} onClose={onClose}>
      <FlexBox direction="column" className="padded">
        <span className="header">Create a Source Book First</span>
        <span>
          Everything you create goes in one of your own source books.
          You don&apos;t have one yet, so you&apos;ll need to create one before you can create a new { item_name }.
          It will be your default source book, so anything you create will go in it unless you choose otherwise.
        </span>
        <StringBox name="Name" value={name} disabled={processing}
          onChange={(value: string) => setName(value)}
        />
        <StringBox name="Abbreviation" value={abbreviation} disabled={processing}
          message={abbreviation.trim() === "" && name.trim() !== "" ? `Will be ${use_abbreviation}` : ""}
          onChange={(value: string) => setAbbreviation(value)}
        />
        <CheckBox name="Public" value={is_public} disabled={processing}
          tooltip="Public source books can be seen by everyone. Homebrew source books are always free."
          onChange={(changed: boolean) => setIsPublic(changed)}
        />
        { error !== "" && <span style={{ color: "red" }}>{ error }</span> }
        <FlexBox direction="row">
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Create Source Book"
              disabled={processing || !api.online || name.trim() === ""}
              onClick={create}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Cancel" disabled={processing} onClick={onClose} />
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </ModalOrDrawer>
  );
}
